import { ContentCheck, ContentDelete, ContentUndo, NoticeDoNot, NoticeInfo } from '@fs/zion-icon'
import { MenuNewWindow } from '@fs/zion-icon/dist/cjs/icons'
import { IconButton, Row, useOverlay } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import { useRouteMatch } from '@fs/zion-router'
import { PermissionsContext } from '../../app/PermissionsContext'
import ApproveArtifactOverlay from '../overlay/ApproveArtifactOverlay'
import DeleteArtifactOverlay from '../overlay/DeleteArtifactOverlay'
import RestoreArtifactOverlay from '../overlay/RestoreArtifactOverlay'
import RestrictArtifactOverlay from '../overlay/RestrictArtifactOverlay'
import { SCREENING_STATE, SCREENING_TAG } from '../util/artifactsConstants'
import { getArtifactInfoPageUrl, getMemoriesUrl, getTombstonedArtifactInfoPageUrl } from '../util/artifactsUtils'

/**
 * Action buttons (restrict, approve, delete, view info) to be shown in an ArtifactCard.
 * Restrict, approve, and delete are all handled here. View Info is different, and just calls
 * the onActionPerformed callback to let ArtifactCard show the Dialog with ArtifactInfo.
 */
const ArtifactActionButtons = ({ artifact, handleActionPerformed }) => {
  const { t } = useTranslation()
  const { isAuthorized } = useContext(PermissionsContext)
  const deleteArtifactOverlay = useOverlay()
  const restrictArtifactOverlay = useOverlay()
  const approveArtifactOverlay = useOverlay()
  const restoreArtifactOverlay = useOverlay()
  const route = useRouteMatch()
  const hideInfoButton = route.path.includes('/artifacts/artifact')

  // This object contains all possible actions, and will be filtered below
  const buttons = {
    approve: {
      title: t('artifacts.approve', 'Approve'),
      icon: ContentCheck,
      color: 'green',
      onClick: approveArtifactOverlay.handleClick,
      dialogOverlay: (
        <ApproveArtifactOverlay
          artifactId={artifact.id}
          onSuccess={handleActionPerformed ? () => handleActionPerformed('approve') : undefined}
          {...approveArtifactOverlay}
        />
      ),
    },
    restrict: {
      title: t('action.restrict', 'Restrict'),
      icon: NoticeDoNot,
      color: 'yellow',
      onClick: restrictArtifactOverlay.handleClick,
      dialogOverlay: (
        <RestrictArtifactOverlay
          artifactId={artifact.id}
          onSuccess={handleActionPerformed ? () => handleActionPerformed('restrict') : undefined}
          {...restrictArtifactOverlay}
        />
      ),
    },
    info: {
      title: t('artifacts.viewInfo', 'View Info'),
      icon: NoticeInfo,
      color: 'blue',
      dialogOverlay: null,
      to:
        artifact.screeningState === SCREENING_STATE.DELETED
          ? getTombstonedArtifactInfoPageUrl(artifact.id, artifact.contributorPatronId)
          : getArtifactInfoPageUrl(artifact.id),
      target: '_blank',
    },
    delete: {
      title: t('action.delete', 'Delete'),
      color: 'red',
      icon: ContentDelete,
      onClick: deleteArtifactOverlay.handleClick,
      dialogOverlay: (
        <DeleteArtifactOverlay
          artifactId={artifact.id}
          patronId={artifact.contributorPatronId}
          screeningState={artifact.screeningState}
          onSuccess={handleActionPerformed ? () => handleActionPerformed('delete') : undefined}
          {...deleteArtifactOverlay}
        />
      ),
    },
    restore: {
      title: t('action.restore', 'Restore'),
      color: 'green',
      icon: ContentUndo,
      onClick: restoreArtifactOverlay.handleClick,
      dialogOverlay: (
        <RestoreArtifactOverlay
          artifactId={artifact.id}
          patronId={artifact.contributorPatronId}
          onSuccess={handleActionPerformed ? () => handleActionPerformed('restore') : undefined}
          {...restoreArtifactOverlay}
        />
      ),
    },
    externalInfo: {
      title: t('artifacts.viewOnFamilysearch', 'View on Familysearch.org'),
      icon: MenuNewWindow,
      // color: 'blue',
      dialogOverlay: null,
      to: getMemoriesUrl(artifact.id),
      target: '_blank',
    },
  }

  // 1. Get relevant buttons based on artifact state
  let stateButtons = []
  switch (artifact.screeningState) {
    case SCREENING_STATE.APPROVED:
      stateButtons = [buttons.restrict, buttons.delete]
      break
    case SCREENING_STATE.RESTRICTED:
      if (artifact.screeningTag === SCREENING_TAG.PORN) {
        if (isAuthorized('ArtPrmCanViewPorn')) {
          stateButtons = [buttons.approve, buttons.delete]
        } else {
          stateButtons = [buttons.delete]
        }
      } else {
        stateButtons = [buttons.approve, buttons.delete]
      }
      break
    case SCREENING_STATE.DELETED:
      stateButtons = [buttons.restore, buttons.delete]
      break
    case SCREENING_STATE.ESCALATE:
      stateButtons = [buttons.approve, buttons.restrict, buttons.delete]
      break
    case SCREENING_STATE.UNSCREENED:
    default:
      stateButtons = [buttons.approve, buttons.delete]
      break
  }

  if (!hideInfoButton) stateButtons.push(buttons.info)
  if (artifact.screeningState !== SCREENING_STATE.DELETED) {
    stateButtons.push(buttons.externalInfo)
  }

  // 2. Get authorized buttons based on permissions
  const authorizedButtons = [buttons.externalInfo]
  if (!hideInfoButton) authorizedButtons.unshift(buttons.info)

  if (isAuthorized('ArtPrmCanRestrictAndApprove')) {
    authorizedButtons.push(buttons.restrict, buttons.approve, buttons.restore)
  }
  if (isAuthorized('ArtPrmCanDeleteOtherUsersData')) {
    authorizedButtons.push(buttons.delete, buttons.restore)
  }

  // 3. Now intersect state-specific buttons and authorized buttons
  let visibleButtons = stateButtons.filter((b) => authorizedButtons.includes(b))

  if (visibleButtons.length === 0) {
    return null
  }

  visibleButtons = visibleButtons.map((button) => {
    return (
      <React.Fragment key={`${artifact.id}-${button.title}`}>
        <IconButton
          title={button.title}
          onClick={button.onClick}
          to={button.to}
          target={button.target}
          Icon={button.icon}
          color={button.color}
        />
        {button.dialogOverlay}
      </React.Fragment>
    )
  })

  return (
    <Row wrap alignX="center">
      {visibleButtons}
    </Row>
  )
}

ArtifactActionButtons.propTypes = {
  artifact: PropTypes.shape({
    id: PropTypes.number.isRequired,
    screeningState: PropTypes.string.isRequired,
  }).isRequired,
  onActionPerformed: PropTypes.func,
}

export default ArtifactActionButtons
